<script setup lang="ts">
import type { ToastMessageOptions } from 'primevue/toast'

import { Beneficiary, Form, PEP } from '~/layers/forms/models'
import { requiredIfValidator, requiredValidator } from '~/utils/validators'

interface Props {
  modelValue: PEP
  validationScope?: string
}

const props = defineProps<Props>()

const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)
const toast = useToast()

const v$ = useValidation(
  () => ({
    isPoliticallyExposed: { required: requiredValidator },
    politicallyExposedByRelative: { required: requiredValidator },
    positionHeld: {
      required: requiredIfValidator(() => model.value.isPoliticallyExposed === true),
    },
    relativeName: {
      required: requiredIfValidator(() => model.value.politicallyExposedByRelative === true),
    },
    relativePositionHeld: {
      required: requiredIfValidator(() => model.value.politicallyExposedByRelative === true),
    },
    relativeRelation: {
      required: requiredIfValidator(() => model.value.politicallyExposedByRelative === true),
    },
  }),
  model,
  {
    $scope: props.validationScope ?? Form.pep,
    $registerAs: Form.pep,
  }
)

const displayTooltipAsMessage = (message?: string) => {
  if (!message) return
  const toastMessageOptions: ToastMessageOptions = {
    group: 'notifications',
    summary: 'Contigo',
    detail: {
      text: message,
      close: () => toast.remove(toastMessageOptions),
    },
    closable: false,
  }
  toast.remove(toastMessageOptions)
  toast.add(toastMessageOptions)
}
</script>

<template>
  <template
    v-for="(_, k) in PEP.questions"
    :key="k"
  >
    <InputContainer
      class="col-span-full"
      :error="v$.financialEntityName?.$error"
      :error-message="v$.financialEntityName?.$errors[0]?.$message.toString()"
    >
      <div>
        {{ PEP.questions[k].question }}
        <template v-if="PEP.questions[k].hint">
          <span
            v-if="$viewport.isGreaterOrEquals('lg')"
            v-tooltip.top="PEP.questions[k].hint"
            class="icon-[ph--info-bold]"
          />
          <span
            v-else
            class="icon-[ph--info-bold] cursor-pointer"
            @click="() => displayTooltipAsMessage(PEP.questions[k].hint)"
          />
        </template>
      </div>
      <div class="flex flex-nowrap gap-4">
        <div class="flex items-center gap-2">
          <PvRadioButton
            v-model="model[k]"
            :input-id="`${k}-true`"
            :name="k"
            :value="true"
          />
          <label for="ingredient1">Si</label>
        </div>
        <div class="flex items-center gap-2">
          <PvRadioButton
            v-model="model[k]"
            :input-id="`${k}-false`"
            :name="k"
            :value="false"
          />
          <label for="ingredient2">No</label>
        </div>
      </div>
    </InputContainer>
    <template v-if="model[k] && PEP.questions[k].enableSections?.includes('pep-info')">
      <InputField
        label="Cargo público que ocupa"
        :error="v$.positionHeld?.$error"
        :error-message="v$.positionHeld?.$errors?.[0]?.$message.toString()"
      >
        <PvInputText v-model="model.positionHeld" />
      </InputField>
    </template>
    <template v-if="model[k] && PEP.questions[k].enableSections?.includes('pep-relative-info')">
      <InputField
        label="Nombre del familiar"
        :error="v$.relativePositionHeld?.$error"
        :error-message="v$.relativePositionHeld?.$errors?.[0]?.$message.toString()"
      >
        <PvInputText v-model="model.relativeName" />
      </InputField>
      <InputField
        label="Parentesco"
        :error="v$.relativeRelation?.$error"
        :error-message="v$.relativeRelation?.$errors?.[0]?.$message.toString()"
      >
        <PvSelect
          v-model="model.relativeRelation"
          :options="Beneficiary.getRelationshipTypeList()"
        />
      </InputField>
      <InputField
        label="Cargo público que ocupa"
        :error="v$.relativePositionHeld?.$error"
        :error-message="v$.relativePositionHeld?.$errors?.[0]?.$message.toString()"
      >
        <PvInputText v-model="model.relativePositionHeld" />
      </InputField>
    </template>
  </template>
</template>
